import {
  BoardState,
  Move,
  Square,
  Color,
  Piece,
  PieceType,
  Capture,
  VARIANT_CONFIGS,
  VARIANT_SPECS,
} from '@draughts/shared';
import type { VariantRulesSpec } from '@draughts/shared';
import { squareToPosition, positionToSquare } from '../board/square-utils.js';
import { getPieceAt } from '../board/board-utils.js';
import { getSimpleMoves, getKingMoves, willPromote } from './simple-moves.js';
import { getBlockedKingState } from './blocked-king.js';
import {
  getManCaptureDirections,
  getKingCaptureDirections,
} from './directions.js';

interface VariantContext {
  boardSize: number;
  darkSquareBottomRight: boolean;
  allSquares: boolean;
  rules: VariantRulesSpec;
}

/**
 * Tutte le sequenze di presa di UN pezzo, senza nessuna priorità di variante.
 *
 * Può contenere prese EQUIVALENTI — stessa partenza, stesso arrivo, stesso insieme
 * catturato, solo un `path` diverso: sono i due versi di una presa ad anello. Le
 * collassa `getAllLegalMoves`, e DOPO i confronti, perché `compareCaptureSequence`
 * guarda l'ordine in cui cadono le dame (vedi `collassaEquivalenti`). Chi chiama
 * questa funzione direttamente deve essere insensibile ai doppioni: oggi lo sono
 * entrambi i chiamanti (`sideHasCapture` legge solo se l'elenco è vuoto, e le
 * pre-mosse viaggiano come insiemi di caselle).
 */
export function getCaptureMoves(board: BoardState, square: Square): Move[] {
  const piece = getPieceAt(board, square);

  if (!piece) {
    return [];
  }

  const variantConfig = VARIANT_CONFIGS[board.variant];
  const ctx: VariantContext = {
    boardSize: variantConfig.size,
    darkSquareBottomRight: variantConfig.darkSquareBottomRight,
    allSquares: variantConfig.playableSquares === variantConfig.size * variantConfig.size,
    rules: VARIANT_SPECS[board.variant].rules,
  };

  const captures: Move[] = [];
  const capturedSquares = new Set<Square>();

  if (piece.type === PieceType.MAN) {
    findManCaptures(board, square, square, [], [], capturedSquares, captures, piece.color, ctx);
  } else {
    findKingCaptures(board, square, square, [], [], capturedSquares, captures, piece.color, ctx);
  }

  return captures;
}

/**
 * True if `square` blocks the capturing piece's transit or landing. The mover's origin is
 * vacated at the first jump (FMJD); under immediate removal (Turkish/Triestine) the squares
 * of pieces already captured in this sequence are free as well. Everywhere else, captured
 * pieces stay on the board as blockers until the move ends (coup turc).
 */
function isSquareBlocked(
  board: BoardState,
  square: Square,
  originalSquare: Square,
  capturedSquares: Set<Square>,
  rules: VariantRulesSpec,
): boolean {
  if (square === originalSquare) return false;
  if (rules.removesCapturedImmediately && capturedSquares.has(square)) return false;
  return !!getPieceAt(board, square);
}

function findManCaptures(
  board: BoardState,
  originalSquare: Square,
  currentSquare: Square,
  capturesSoFar: Capture[],
  pathSoFar: Square[],
  capturedSquares: Set<Square>,
  allMoves: Move[],
  pieceColor: Color,
  ctx: VariantContext,
): void {
  const { boardSize, darkSquareBottomRight, allSquares, rules } = ctx;
  const currentPos = squareToPosition(currentSquare, boardSize, darkSquareBottomRight, allSquares);
  let foundCapture = false;

  const directions = getManCaptureDirections(pieceColor, rules, allSquares);

  for (const dir of directions) {
    const enemyPos = {
      row: currentPos.row + dir.row,
      col: currentPos.col + dir.col,
    };
    const enemySquare = positionToSquare(enemyPos, boardSize, darkSquareBottomRight, allSquares);

    if (!enemySquare) continue;

    const enemyPiece = getPieceAt(board, enemySquare);
    if (!enemyPiece || enemyPiece.color === pieceColor) continue;

    if (!rules.menCanCaptureKing && enemyPiece.type === PieceType.KING) continue;

    if (capturedSquares.has(enemySquare)) continue;

    const landingPos = {
      row: currentPos.row + dir.row * 2,
      col: currentPos.col + dir.col * 2,
    };
    const landingSquare = positionToSquare(landingPos, boardSize, darkSquareBottomRight, allSquares);

    if (!landingSquare) continue;
    if (isSquareBlocked(board, landingSquare, originalSquare, capturedSquares, rules)) continue;

    foundCapture = true;

    const newCapture: Capture = {
      square: enemySquare,
      piece: enemyPiece,
    };

    const newCapturesSoFar = [...capturesSoFar, newCapture];
    const newPathSoFar = [...pathSoFar, landingSquare];
    const newCapturedSquares = new Set(capturedSquares);
    newCapturedSquares.add(enemySquare);

    const landingPosForPromo = squareToPosition(landingSquare, boardSize, darkSquareBottomRight, allSquares);
    const isPromotionRow = willPromote(pieceColor, landingPosForPromo.row, boardSize);

    if (isPromotionRow && rules.promotionStopsCapture) {
      allMoves.push({
        from: originalSquare,
        to: landingSquare,
        captures: newCapturesSoFar,
        isPromotion: true,
        path: newPathSoFar.length > 1 ? newPathSoFar : undefined,
      });
    } else if (isPromotionRow && rules.promoteInPassing) {
      // Russian 'promotion in passing': the man becomes a king right now and must
      // continue the capture as a flying king (terminal moves are promotions).
      findKingCaptures(
        board,
        originalSquare,
        landingSquare,
        newCapturesSoFar,
        newPathSoFar,
        newCapturedSquares,
        allMoves,
        pieceColor,
        ctx,
        true,
      );
    } else {
      findManCaptures(
        board,
        originalSquare,
        landingSquare,
        newCapturesSoFar,
        newPathSoFar,
        newCapturedSquares,
        allMoves,
        pieceColor,
        ctx,
      );
    }
  }

  if (!foundCapture && capturesSoFar.length > 0) {
    const finalPos = squareToPosition(currentSquare, boardSize, darkSquareBottomRight, allSquares);
    const isPromotion = willPromote(pieceColor, finalPos.row, boardSize);

    allMoves.push({
      from: originalSquare,
      to: currentSquare,
      captures: capturesSoFar,
      isPromotion,
      path: pathSoFar.length > 1 ? pathSoFar : undefined,
    });
  }
}

function findKingCaptures(
  board: BoardState,
  originalSquare: Square,
  currentSquare: Square,
  capturesSoFar: Capture[],
  pathSoFar: Square[],
  capturedSquares: Set<Square>,
  allMoves: Move[],
  pieceColor: Color,
  ctx: VariantContext,
  // True when this king-capture subtree started from a man that promoted mid-capture
  // (Russian 'promotion in passing'): terminal moves are then promotions.
  promoted = false,
  // Direction of the previous jump in this sequence (Turkish forbidCaptureReversal:
  // continuing straight or turning 90° is fine, reversing 180° is not).
  lastDir: { row: number; col: number } | null = null,
): void {
  const { boardSize, darkSquareBottomRight, allSquares, rules } = ctx;
  const currentPos = squareToPosition(currentSquare, boardSize, darkSquareBottomRight, allSquares);
  let foundCapture = false;

  const directions = getKingCaptureDirections(rules, allSquares);

  for (const dir of directions) {
    if (
      rules.forbidCaptureReversal &&
      lastDir &&
      dir.row === -lastDir.row &&
      dir.col === -lastDir.col
    ) {
      continue;
    }
    if (!rules.flyingKings) {
      const enemyPos = {
        row: currentPos.row + dir.row,
        col: currentPos.col + dir.col,
      };
      const enemySquare = positionToSquare(enemyPos, boardSize, darkSquareBottomRight, allSquares);

      if (!enemySquare) continue;

      const enemyPiece = getPieceAt(board, enemySquare);
      if (!enemyPiece || enemyPiece.color === pieceColor) continue;
      if (capturedSquares.has(enemySquare)) continue;

      const landingPos = {
        row: currentPos.row + dir.row * 2,
        col: currentPos.col + dir.col * 2,
      };
      const landingSquare = positionToSquare(landingPos, boardSize, darkSquareBottomRight, allSquares);

      if (!landingSquare) continue;
      if (isSquareBlocked(board, landingSquare, originalSquare, capturedSquares, rules)) continue;

      foundCapture = true;

      const newCapture: Capture = {
        square: enemySquare,
        piece: enemyPiece,
      };

      const newCapturesSoFar = [...capturesSoFar, newCapture];
      const newPathSoFar = [...pathSoFar, landingSquare];
      const newCapturedSquares = new Set(capturedSquares);
      newCapturedSquares.add(enemySquare);

      findKingCaptures(
        board,
        originalSquare,
        landingSquare,
        newCapturesSoFar,
        newPathSoFar,
        newCapturedSquares,
        allMoves,
        pieceColor,
        ctx,
        promoted,
        dir,
      );
    } else {
      let distance = 1;
      let enemySquare: Square | null = null;
      let enemyPiece = null;

      while (true) {
        const checkPos = {
          row: currentPos.row + dir.row * distance,
          col: currentPos.col + dir.col * distance,
        };
        const checkSquare = positionToSquare(checkPos, boardSize, darkSquareBottomRight, allSquares);

        if (!checkSquare) break;

        if (isSquareBlocked(board, checkSquare, originalSquare, capturedSquares, rules)) {
          const piece = getPieceAt(board, checkSquare)!;
          if (piece.color === pieceColor) {
            break;
          } else if (!capturedSquares.has(checkSquare)) {
            enemySquare = checkSquare;
            enemyPiece = piece;
            distance++;
            break;
          } else {
            // Already captured, delayed removal: the ghost blocks the ray (coup turc).
            break;
          }
        }

        distance++;
      }

      if (!enemySquare || !enemyPiece) continue;

      while (true) {
        const landingPos = {
          row: currentPos.row + dir.row * distance,
          col: currentPos.col + dir.col * distance,
        };
        const landingSquare = positionToSquare(landingPos, boardSize, darkSquareBottomRight, allSquares);

        if (!landingSquare) break;
        if (isSquareBlocked(board, landingSquare, originalSquare, capturedSquares, rules)) break;

        foundCapture = true;

        const newCapture: Capture = {
          square: enemySquare,
          piece: enemyPiece,
        };

        const newCapturesSoFar = [...capturesSoFar, newCapture];
        const newPathSoFar = [...pathSoFar, landingSquare];
        const newCapturedSquares = new Set(capturedSquares);
        newCapturedSquares.add(enemySquare);

        findKingCaptures(
          board,
          originalSquare,
          landingSquare,
          newCapturesSoFar,
          newPathSoFar,
          newCapturedSquares,
          allMoves,
          pieceColor,
          ctx,
          promoted,
          dir,
        );

        distance++;
      }
    }
  }

  if (!foundCapture && capturesSoFar.length > 0) {
    allMoves.push({
      from: originalSquare,
      to: currentSquare,
      captures: capturesSoFar,
      isPromotion: promoted,
      path: pathSoFar.length > 1 ? pathSoFar : undefined,
    });
  }
}

/**
 * Regola Frisian "dama bloccata": una dama può muovere al massimo
 * `rules.blockedKingMaxMoves` volte di fila senza catturare; superata la soglia il giocatore
 * deve muovere un altro pezzo.
 *
 * LE CONDIZIONI DELLA REGOLA NON STANNO PIU' QUI: le dice `getBlockedKingState`, perché le
 * legge anche la damiera a schermo per i pallini sotto la dama (uno per mossa consecutiva).
 * Là dentro ci sono il gate di variante, l'esenzione di chi non ha più pedine (art. 17), e
 * la presenza della dama sulla casella della striscia.
 *
 * Qui resta solo ciò che riguarda la GENERAZIONE DI MOSSE: il filtro delle mosse silenziose
 * della dama bloccata (le catture hanno comunque la precedenza).
 *
 * NIENTE FALLBACK. C'era: se il filtro svuotava del tutto l'insieme, non veniva applicato,
 * «per evitare una falsa sconfitta». Serviva alla dama SOLA, che oggi è esente a monte; e
 * il caso che gli restava — pedine in campo ma tutte immobili — non è una falsa sconfitta,
 * è una sconfitta: art. 20f, «It is his move but he cannot make a legal move». La lista
 * vuota è letta da `determineGameOutcome`, che passa da questo stesso `getAllLegalMoves`,
 * quindi diventa un risultato (`no_moves`) e non una partita congelata.
 */
function applyBlockedKingRule(board: BoardState, simpleMoves: Move[]): Move[] {
  const state = getBlockedKingState(
    board.pieces,
    board.variant,
    board.currentTurn,
    board.kingMoveStreak,
  );
  if (!state?.blocked) return simpleMoves;

  return simpleMoves.filter((m) => m.from !== state.square);
}

/**
 * Le mosse legali di UN pezzo, secondo le regole della variante.
 *
 * Deriva da `getAllLegalMoves` filtrando per casella di partenza, e non calcola
 * niente per conto proprio. Prima faceva il contrario: prendeva le prese di quel
 * pezzo e teneva le più LUNGHE, senza mai guardare `captureCompareStrategy`,
 * `preferKingCaptureOnTie` o `compareCaptureSequence`. Su una variante dove la
 * scelta non si fa sul numero — l'Inglese, che non impone la presa massima, o la
 * Frisone, che confronta il VALORE — restituiva mosse diverse da quelle vere: in
 * Inglese la presa singola legittima spariva, e la dama bloccata dalla regola
 * frisone non veniva filtrata affatto.
 *
 * `game-store` lo faceva già a mano proprio per questo (con tanto di commento che
 * spiega perché questa funzione non si può usare). Ora la funzione dice la
 * verità, e chi la chiama non deve più saperlo.
 */
export function getLegalMovesForPiece(board: BoardState, square: Square): Move[] {
  if (!getPieceAt(board, square)) return [];
  return getAllLegalMoves(board).filter((m) => m.from === square);
}

/**
 * Mosse legali per il colore corrente — chain di filtri pilotata dalla matrice:
 *   1. captureCompareStrategy: maxPieces | maxValue (Frisian) | maxWithKings | none
 *   2. preferKingCaptureOnTie
 *   3. maxWithKings → max numero di dame catturate
 *   4. compareCaptureSequence → si confrontano le sequenze: vince la presa le cui
 *      dame cadono ai salti più bassi, la prima e poi, a parità, la seconda e così
 *      via (FID 1.1.6.9-10; Triestina h-i). Non basta la prima dama.
 *   5. collassaEquivalenti → le prese che differiscono SOLO nel cammino diventano
 *      una. Ultimo per necessità: il punto 4 guarda l'ordine delle catture.
 */
export function getAllLegalMoves(board: BoardState): Move[] {
  const rules = VARIANT_SPECS[board.variant].rules;
  const currentColorPieces = board.pieces.filter(
    (p) => p.color === board.currentTurn
  );

  interface CaptureWithPieceType {
    move: Move;
    pieceType: PieceType;
    kingsCaptured: number;
    /** Salti (0-based, in ordine di presa) in cui cade una dama. */
    kingSlots: number[];
  }

  const allCaptures: CaptureWithPieceType[] = [];
  const allSimpleMoves: Move[] = [];

  for (const piece of currentColorPieces) {
    const captures = getCaptureMoves(board, piece.square);

    for (const capture of captures) {
      const kingSlots: number[] = [];
      capture.captures.forEach((c, i) => {
        if (c.piece.type === PieceType.KING) kingSlots.push(i);
      });
      allCaptures.push({
        move: capture,
        pieceType: piece.type,
        kingsCaptured: kingSlots.length,
        kingSlots,
      });
    }

    if (captures.length === 0) {
      if (piece.type === PieceType.MAN) {
        allSimpleMoves.push(...getSimpleMoves(board, piece.square));
      } else {
        allSimpleMoves.push(...getKingMoves(board, piece.square));
      }
    }
  }

  if (allCaptures.length === 0) {
    return applyBlockedKingRule(board, allSimpleMoves);
  }

  let filtered = allCaptures;
  const keepMax = (
    items: CaptureWithPieceType[],
    score: (c: CaptureWithPieceType) => number,
  ): CaptureWithPieceType[] => {
    const max = Math.max(...items.map(score));
    return items.filter((c) => score(c) === max);
  };

  switch (rules.captureCompareStrategy) {
    case 'none':
      break;
    case 'maxPieces':
    case 'maxWithKings':
      filtered = keepMax(filtered, (c) => c.move.captures.length);
      break;
    case 'maxValue': {
      const m = rules.manCaptureValue;
      const k = rules.kingCaptureValue;
      filtered = keepMax(
        filtered,
        (c) => c.kingsCaptured * k + (c.move.captures.length - c.kingsCaptured) * m,
      );
      break;
    }
  }

  if (rules.preferKingCaptureOnTie) {
    const kingPieces = filtered.filter((c) => c.pieceType === PieceType.KING);
    if (kingPieces.length > 0) filtered = kingPieces;
  }

  if (rules.captureCompareStrategy === 'maxWithKings') {
    filtered = keepMax(filtered, (c) => c.kingsCaptured);
  }

  if (rules.compareCaptureSequence) {
    // Confronto da vocabolario sui salti delle dame: a questo punto tutte le prese
    // rimaste hanno lo stesso numero di dame (maxWithKings), quindi elenchi della
    // stessa lunghezza. Se una variante attivasse questo criterio senza
    // maxWithKings, l'elenco più corto che è prefisso dell'altro perderebbe: la
    // presa con più dame, coerente con «i pezzi di maggior valore».
    const compareSlots = (a: number[], b: number[]): number => {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        const d = (a[i] as number) - (b[i] as number);
        if (d !== 0) return d;
      }
      return b.length - a.length;
    };
    const best = filtered.reduce((acc, c) =>
      compareSlots(c.kingSlots, acc.kingSlots) < 0 ? c : acc,
    );
    filtered = filtered.filter((c) => compareSlots(c.kingSlots, best.kingSlots) === 0);
  }

  return collassaEquivalenti(filtered.map((c) => c.move));
}

/**
 * Collassa le prese EQUIVALENTI: stessa partenza, stesso arrivo, stesso insieme
 * catturato e stessa promozione, cioè la stessa posizione risultante. Cambia solo
 * il `path`, che `applyMove` non legge nemmeno — è l'animazione (vedi la nota su
 * `Move.path` e la stessa constatazione in `replayLine`).
 *
 * Il generatore le produce davvero, e non per sbaglio: una presa ad ANELLO si
 * percorre nei due versi, e sono due cammini diversi che mangiano gli stessi pezzi
 * e finiscono dove hanno cominciato. Nella frisone — dove le pedine mangiano in
 * tutte e 8 le direzioni — nelle posizioni con catture il 20% delle mosse generate
 * erano doppioni, e l'elenco di una posizione d'anello era letteralmente la stessa
 * mossa scritta due volte. Chi contava i candidati per capire se una casella fosse
 * ambigua trovava 2 dove la scelta non esisteva, e l'interfaccia si bloccava.
 *
 * `isPromotion` deve stare nella chiave: con `promoteInPassing` (russa) due cammini
 * con lo stesso insieme catturato possono promuovere o no, e quelle sono mosse
 * diverse per davvero.
 *
 * PERCHÉ QUI E NON IN `getCaptureMoves`. La catena di `getAllLegalMoves` ha un
 * filtro che punteggia sull'ORDINE delle catture, non sull'insieme:
 * `compareCaptureSequence` (italiana e triestina) confronta i salti in cui cadono
 * le dame. I due versi di un anello hanno quegli indici invertiti, quindi
 * collassarli PRIMA di quel confronto cambia quali insiemi sopravvivono — in
 * italiana fa sparire una linea legale in una posizione e ne fa apparire una
 * illegale in un'altra (c'è un test che lo fissa). Qui invece i sopravvissuti hanno
 * già passato ogni criterio sensibile all'ordine, quindi ciò che resta a pari
 * merito è davvero interscambiabile.
 */
function collassaEquivalenti(moves: Move[]): Move[] {
  if (moves.length < 2) return moves;
  const perChiave = new Map<string, Move>();
  for (const m of moves) {
    const catture = m.captures.map((c) => c.square).sort((a, b) => a - b).join(',');
    const chiave = `${m.from}>${m.to}|${catture}|${m.isPromotion ? 1 : 0}`;
    // Vince la PRIMA: il cammino che si tiene è quello che il generatore ha trovato
    // per primo, così l'animazione è stabile fra una chiamata e l'altra.
    if (!perChiave.has(chiave)) perChiave.set(chiave, m);
  }
  // Nessun doppione: si restituisce l'array originale, senza ricopiarlo. È la
  // strada calda del minimax.
  return perChiave.size === moves.length ? moves : [...perChiave.values()];
}
